import * as signalR from '@microsoft/signalr';

interface NotificationHandler {
  eventType: string;
  handler: (data: any) => void;
}

class SignalRService {
  private connection: signalR.HubConnection | null = null;
  private handlers: NotificationHandler[] = [];
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private isAuthenticationError = false;

  async start(accessToken: string): Promise<void> {
    if (this.connection && this.isConnected) {
      console.log('SignalR already connected');
      return;
    }

    const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:5010';
    const hubUrl = `${baseUrl}/hubs/notifications`;

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => accessToken,
        transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling,
        skipNegotiation: false
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          // Stop reconnection attempts on authentication errors (401)
          if (this.isAuthenticationError) {
            return null; // Stop reconnection
          }

          if (retryContext.previousRetryCount < 3) {
            return 2000; // 2 seconds
          } else if (retryContext.previousRetryCount < 6) {
            return 5000; // 5 seconds
          } else {
            return 10000; // 10 seconds
          }
        }
      })
      .configureLogging(
        // Use Error level to suppress 401 warnings in production
        import.meta.env.MODE === 'production' ? signalR.LogLevel.Error : signalR.LogLevel.Information
      )
      .build();

    // Handle connection events
    this.connection.onclose(() => {
      console.log('SignalR connection closed');
      this.isConnected = false;
    });

    this.connection.onreconnecting((error) => {
      if (import.meta.env.MODE === 'development') {
        console.log('SignalR reconnecting...', error);
      }
      this.isConnected = false;
    });

    this.connection.onreconnected(() => {
      console.log('SignalR reconnected');
      this.isConnected = true;
      this.reconnectAttempts = 0;
    });

    // Set up generic notification receiver
    this.connection.on('ReceiveNotification', (eventType: string, data: any) => {
      // Only log notification data in development to prevent exposing sensitive information
      if (import.meta.env.MODE === 'development') {
        console.log(`Received ${eventType} notification:`, data);
      }

      // Call all registered handlers for this event type
      this.handlers
        .filter(h => h.eventType === eventType)
        .forEach(h => h.handler(data));
    });

    // Set up typing indicator receiver
    this.connection.on('UserTyping', (ticketId: string, userName: string) => {
      if (import.meta.env.MODE === 'development') {
        console.log(`User ${userName} is typing on ticket ${ticketId}`);
      }

      // Call all registered handlers for typing indicator
      this.handlers
        .filter(h => h.eventType === 'UserTyping')
        .forEach(h => h.handler({ ticketId, userName }));
    });

    try {
      await this.connection.start();
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.isAuthenticationError = false;
      // Only log connection success in development
      if (import.meta.env.MODE === 'development') {
        console.log('SignalR connected successfully');
      }
    } catch (error: any) {
      this.isConnected = false;

      // Check if this is a 401 Unauthorized error
      const errorMessage = error?.message || error?.toString() || '';
      const is401Error = errorMessage.includes('401') || errorMessage.includes('Unauthorized');

      if (is401Error) {
        // Set flag to stop reconnection attempts
        this.isAuthenticationError = true;

        // Suppress console spam - only log once in development
        if (import.meta.env.MODE === 'development') {
          console.log('SignalR authentication failed - token may be expired. Auto-logout will handle this.');
        }

        // Don't attempt to reconnect on auth errors
        return;
      }

      // Log non-auth errors normally
      console.error('SignalR connection error:', error);

      // Attempt to reconnect after a delay for non-auth errors
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => {
          console.log(`Attempting to reconnect (attempt ${this.reconnectAttempts})...`);
          this.start(accessToken);
        }, 5000);
      }
    }
  }

  async stop(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log('SignalR connection stopped');
        this.isConnected = false;
        this.isAuthenticationError = false;
        this.handlers = [];
      } catch (error) {
        console.error('Error stopping SignalR connection:', error);
      }
    }
  }

  on(eventType: string, handler: (data: any) => void): void {
    this.handlers.push({ eventType, handler });
  }

  off(eventType: string, handler: (data: any) => void): void {
    this.handlers = this.handlers.filter(
      h => !(h.eventType === eventType && h.handler === handler)
    );
  }

  async subscribeToTicket(ticketId: string): Promise<void> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('SubscribeToTicket', ticketId);
        if (import.meta.env.MODE === 'development') {
          console.log(`Subscribed to ticket ${ticketId}`);
        }
      } catch (error) {
        console.error(`Error subscribing to ticket ${ticketId}:`, error);
      }
    }
  }

  async unsubscribeFromTicket(ticketId: string): Promise<void> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('UnsubscribeFromTicket', ticketId);
        if (import.meta.env.MODE === 'development') {
          console.log(`Unsubscribed from ticket ${ticketId}`);
        }
      } catch (error) {
        console.error(`Error unsubscribing from ticket ${ticketId}:`, error);
      }
    }
  }

  async sendTypingIndicator(ticketId: string, userName: string): Promise<void> {
    if (this.connection && this.isConnected) {
      try {
        await this.connection.invoke('SendTypingIndicator', ticketId, userName);
      } catch (error) {
        console.error('Error sending typing indicator:', error);
      }
    }
  }

  getConnectionState(): signalR.HubConnectionState | null {
    return this.connection?.state || null;
  }

  isConnectionActive(): boolean {
    return this.isConnected && this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

// Export singleton instance
export const signalRService = new SignalRService();
